import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Search } from 'lucide-react';
import type { Account } from '../../types/account';
import { WebhookHealthCard } from './WebhookHealthCard';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Community Inbox "Manage accounts" panel — READ-ONLY (2026-07-24). Enablement
// moved off this dialog: communityEnabled is now DERIVED from the
// community_management service (like runsMeta from meta_ads), managed on the
// account's Services tab. This panel keeps the two things that DO belong to the
// inbox surface — the Zernio webhook health card (register / re-register / send
// test) and an informational list of the currently-enabled accounts, each a
// deep-link to its Services tab where enablement is toggled.

export function ManageAccountsDialog({
  open,
  onOpenChange,
  accounts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: Account[];
}) {
  const [search, setSearch] = useState('');

  // Only ENABLED accounts are listed (this panel no longer enables/disables).
  const enabled = useMemo(
    () =>
      accounts
        .filter((a) => a.communityEnabled ?? false)
        .filter(
          (a) => search.trim() === '' || a.name.toLowerCase().includes(search.trim().toLowerCase()),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accounts, search],
  );
  const enabledCount = useMemo(
    () => accounts.filter((a) => a.communityEnabled ?? false).length,
    [accounts],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Community accounts</DialogTitle>
          <DialogDescription>
            {enabledCount} account{enabledCount === 1 ? '' : 's'} enabled for the inbox. To enable or
            disable community management, tick <strong>Community Management</strong> on the account's
            Services tab.
          </DialogDescription>
        </DialogHeader>

        {/* The Zernio webhook health + Re-register / Send test controls live here
            (still the inbox's responsibility, unlike account enablement). */}
        <WebhookHealthCard />

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search enabled accounts…"
            className="pl-8"
          />
        </div>

        <div className="scroll-subtle max-h-[45vh] overflow-y-auto rounded-md border border-slate-200">
          {enabled.map((account) => (
            <Link
              key={account.id}
              to={`/accounts/${account.id}?tab=services`}
              className="group flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0 hover:bg-slate-50"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800 group-hover:text-kyck-700">
                {account.name}
              </span>
              <Badge variant="outline" className="border-kyck-200 bg-kyck-50 text-kyck-700">
                Enabled
              </Badge>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-kyck-600" />
            </Link>
          ))}
          {enabled.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">
              {search.trim()
                ? 'No enabled accounts match.'
                : 'No accounts are enabled yet. Tick Community Management on an account’s Services tab.'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
